import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SettingsProvider, useSettings } from "@/context/SettingsContext";
import AppLayout from "@/components/layout/AppLayout";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import { CabangPage, WilayahPage } from "@/pages/OfficePages";
import SettingsPage from "@/pages/SettingsPage";
import PasswordPage from "@/pages/PasswordPage";
import StaffPage from "@/pages/StaffPage";
import MembersPage from "@/pages/MembersPage";
import MemberDetailPage from "@/pages/MemberDetailPage";
import TransaksiPage from "@/pages/TransaksiPage";
import LoansPage from "@/pages/LoansPage";
import RiwayatPage from "@/pages/RiwayatPage";
import LaporanPage from "@/pages/LaporanPage";
import ResortPage from "@/pages/ResortPage";
import ModalPage from "@/pages/ModalPage";
import BiayaAdminPage from "@/pages/BiayaAdminPage";
import ResortDetailPage from "@/pages/ResortDetailPage";
import KeuanganPage from "@/pages/KeuanganPage";
import LaporanResortPage from "@/pages/LaporanResortPage";
import StatusPetugasPage from "@/pages/StatusPetugasPage";
import LaporanHarianPage from "@/pages/LaporanHarianPage";
import LiveTrackingPage from "@/pages/LiveTrackingPage";
import AbsensiPage from "@/pages/AbsensiPage";
import TagihanPage from "@/pages/TagihanPage";
import PengaturanAbsensiPage from "@/pages/PengaturanAbsensiPage";
import PengaturanCabangPage from "@/pages/PengaturanCabangPage";
import DatabasePage from "@/pages/DatabasePage";
import ViewerPage from "@/pages/ViewerPage";
import OfficeReportPage from "@/pages/OfficeReportPage";
import PengaturanKataPage from "@/pages/PengaturanKataPage";
import LayananServerPage from "@/pages/LayananServerPage";
import LoanContractPage from "@/pages/LoanContractPage";
import AturLaporanPage, { AturLaporanViewer } from "@/pages/AturLaporanPage";
import DataPetugasPage from "@/pages/DataPetugasPage";
import IdCardPage from "@/pages/IdCardPage";
import ProfilePage from "@/pages/ProfilePage";
import { MetricsBoard } from "@/components/MetricsBoard";
import TagihanLayananPage from "@/pages/TagihanLayananPage";
import TagihanLayananDetailPage, { TagihanWilayahPage } from "@/pages/TagihanLayananDetailPage";

const CABANG = ["admin_cabang", "kepala_bagian", "kasir", "petugas"];

const Protected = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-muted-foreground" data-testid="app-loading">Memuat...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <AppLayout />;
};

const RoleGate = ({ roles, menu, children }) => {
  const { user } = useAuth();
  const { settings } = useSettings();
  const hidden = menu && settings.hidden_menus.includes(menu);
  return roles.includes(user.role) && !hidden ? children : <Navigate to="/dashboard" replace />;
};

const ThemedToaster = () => {
  const { dark } = useSettings();
  return <Toaster richColors position="top-center" theme={dark ? "dark" : "light"} />;
};

function App() {
  return (
    <SettingsProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route element={<Protected />}>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/worksheet/wilayah" element={<RoleGate roles={["super_admin"]} menu="wilayah"><WilayahPage /></RoleGate>} />
              <Route path="/pengaturan" element={<RoleGate roles={["super_admin"]}><SettingsPage /></RoleGate>} />
              <Route path="/pengaturan-database" element={<RoleGate roles={["super_admin"]}><DatabasePage /></RoleGate>} />
              <Route path="/tagihan-layanan" element={<RoleGate roles={["super_admin"]}><TagihanLayananPage /></RoleGate>} />
              <Route path="/tagihan-layanan/:wid" element={<RoleGate roles={["super_admin"]}><TagihanLayananDetailPage /></RoleGate>} />
              <Route path="/tagihan-wilayah" element={<RoleGate roles={["admin_wilayah"]}><TagihanWilayahPage /></RoleGate>} />
              <Route path="/layanan-server" element={<RoleGate roles={["super_admin"]}><LayananServerPage /></RoleGate>} />
              <Route path="/pengaturan-kata" element={<RoleGate roles={["admin_wilayah", "viewer"]} menu="pengaturan_kata"><PengaturanKataPage /></RoleGate>} />
              <Route path="/laporan-viewer" element={<RoleGate roles={["viewer"]}><MetricsBoard title="Laporan" editPath="/atur-laporan" /></RoleGate>} />
              <Route path="/atur-laporan" element={<RoleGate roles={["viewer"]}><AturLaporanViewer /></RoleGate>} />
              <Route path="/atur-dashboard" element={<RoleGate roles={["admin_wilayah", "admin_cabang"]}><AturLaporanPage /></RoleGate>} />
              <Route path="/data-petugas" element={<RoleGate roles={["admin_wilayah"]}><DataPetugasPage /></RoleGate>} />
              <Route path="/id-card" element={<RoleGate roles={["viewer"]}><IdCardPage /></RoleGate>} />
              <Route path="/profil" element={<RoleGate roles={["viewer", "admin_wilayah", "admin_cabang", "kepala_bagian", "kasir", "petugas"]}><ProfilePage /></RoleGate>} />
              <Route path="/akun-viewer" element={<RoleGate roles={["super_admin"]}><ViewerPage /></RoleGate>} />
              <Route path="/laporan-kantor/resort/:rid" element={<RoleGate roles={["super_admin", "admin_wilayah", "viewer"]}><ResortDetailPage key="office-resort" /></RoleGate>} />
              <Route path="/laporan-kantor/:unit/:id" element={<RoleGate roles={["super_admin", "admin_wilayah", "viewer"]}><OfficeReportPage /></RoleGate>} />
              <Route path="/worksheet/cabang" element={<RoleGate roles={["admin_wilayah"]} menu="cabang"><CabangPage /></RoleGate>} />
              <Route path="/petugas" element={<RoleGate roles={["admin_cabang"]} menu="petugas"><StaffPage key="staff" /></RoleGate>} />
              <Route path="/anggota" element={<RoleGate roles={CABANG} menu="anggota"><MembersPage /></RoleGate>} />
              <Route path="/anggota/:nik" element={<RoleGate roles={CABANG}><MemberDetailPage /></RoleGate>} />
              <Route path="/transaksi" element={<Navigate to="/simpanan" replace />} />
              <Route path="/simpanan" element={<RoleGate roles={["kasir", "petugas", "admin_cabang"]} menu="simpanan"><TransaksiPage key="simpanan" modes={["setor", "tarik"]} title="Simpanan" /></RoleGate>} />
              <Route path="/angsuran" element={<RoleGate roles={["kasir", "petugas", "admin_cabang"]} menu="angsuran"><TransaksiPage key="angsuran" modes={["angsuran"]} title="Angsuran" /></RoleGate>} />
              <Route path="/pinjaman/:id/kontrak" element={<RoleGate roles={CABANG}><LoanContractPage /></RoleGate>} />
              <Route path="/pinjaman" element={<RoleGate roles={CABANG} menu="pinjaman"><LoansPage /></RoleGate>} />
              <Route path="/riwayat" element={<RoleGate roles={CABANG} menu="riwayat"><RiwayatPage /></RoleGate>} />
              <Route path="/laporan" element={<RoleGate roles={["admin_cabang", "kepala_bagian"]} menu="laporan"><LaporanPage /></RoleGate>} />
              <Route path="/ubah-password" element={<PasswordPage />} />
              <Route path="/resort/:rid" element={<RoleGate roles={CABANG}><ResortDetailPage /></RoleGate>} />
              <Route path="/keuangan" element={<RoleGate roles={["kasir", "admin_cabang"]} menu="keuangan"><KeuanganPage /></RoleGate>} />
              <Route path="/laporan-resort" element={<RoleGate roles={["kasir", "admin_cabang", "kepala_bagian"]} menu="laporan_resort"><LaporanResortPage /></RoleGate>} />
              <Route path="/status-petugas" element={<RoleGate roles={["kepala_bagian", "admin_cabang"]} menu="status_petugas"><StatusPetugasPage /></RoleGate>} />
              <Route path="/akun-tersembunyi" element={<RoleGate roles={["admin_cabang"]} menu="akun_tersembunyi"><StaffPage key="hidden" hiddenView /></RoleGate>} />
              <Route path="/resort" element={<RoleGate roles={["admin_cabang", "kepala_bagian"]} menu="resort"><ResortPage /></RoleGate>} />
              <Route path="/laporan-harian" element={<RoleGate roles={["petugas"]} menu="laporan_harian"><LaporanHarianPage /></RoleGate>} />
              <Route path="/live-tracking" element={<RoleGate roles={["admin_cabang", "kepala_bagian"]} menu="live_tracking"><LiveTrackingPage /></RoleGate>} />
              <Route path="/absensi" element={<RoleGate roles={["admin_cabang", "kepala_bagian", "kasir", "petugas", "admin_wilayah"]} menu="absensi"><AbsensiPage /></RoleGate>} />
              <Route path="/resort/:rid/tagihan" element={<RoleGate roles={CABANG}><TagihanPage /></RoleGate>} />
              <Route path="/pengaturan-absensi" element={<RoleGate roles={["admin_cabang"]} menu="pengaturan_absensi"><PengaturanAbsensiPage /></RoleGate>} />
              <Route path="/pengaturan-cabang" element={<RoleGate roles={["admin_cabang"]}><PengaturanCabangPage /></RoleGate>} />
              <Route path="/modal" element={<RoleGate roles={CABANG} menu="modal"><ModalPage /></RoleGate>} />
              <Route path="/biaya-admin" element={<RoleGate roles={["super_admin", "admin_wilayah", "admin_cabang", "kepala_bagian", "kasir"]} menu="biaya_admin"><BiayaAdminPage /></RoleGate>} />
            </Route>
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
        <ThemedToaster />
      </AuthProvider>
    </SettingsProvider>
  );
}

export default App;
